/**
 * The shared cutscene schema (SF23.1).
 *
 * Generalises sstk-dwindling's game-local
 * `src/the-dwindling/cutscenes/timeline.ts` (actors/post/audio/signals
 * tracks) into this shared engine, plus a new camera track. That module's
 * own comment explicitly deferred the camera track: "the follow-on is a
 * small, additive change to that existing pattern [FirstPersonController's
 * editor focus-pan] in the shared engine, not new engineering invented
 * here." `externalCameraOverrideRef` on `FirstPersonController` is that
 * additive change; `CameraKey` here is its authoring-time counterpart.
 *
 * Three generalisations beyond Dwindling's own version:
 * - `ActorKey` carries its own `actorId` - a Studio project can have more
 *   than one performer (the player, an NPC, a retargeted custom rig);
 *   Dwindling's Being is always the one actor, so its `ActorKey` never
 *   needed one.
 * - The camera track: `{ t, mode, approach, lookAt }`, mirroring the map
 *   editor's own `FocusTarget` shape (`editor/store/mapEditorStore.ts`) so
 *   the runtime player drives the same pan-and-look mechanic that focus
 *   target already proved out for editor camera moves.
 * - A camera key can also track a live actor (`trackActorId`) instead of a
 *   fixed world-space target - a horror game's antagonist mid-chase, or any
 *   actor whose position is not known at author time, still needs its face
 *   framed correctly regardless of where it actually ends up standing. This
 *   is the one case fixed `approach`/`lookAt` cannot express at all: it
 *   generalises Dwindling's own snap.ts, whose camera track was
 *   deliberately actor-relative (distance/heightOffset off the actor's own
 *   head) for exactly this reason.
 *
 * Pure and side-effect-free by design, same as the Dwindling original: every
 * sampling function here takes a time and returns what should be true at
 * that time, or what newly became due since the last frame. The runtime
 * player (SF23.2) owns the clock and calls into the actual
 * mixer/audio/camera/logic systems; this module only computes what those
 * calls should be, so it is testable without a renderer or an audio
 * context.
 */

/** A named beat on an actor's track: play this clip, on this actor, from
 * here on - held until the next key for the same actorId. */
export interface ActorKey {
  t: number;
  /** Which character this clip plays on - a project roster id, or
   * 'player' for the player character. */
  actorId: string;
  clipId: string;
}

/**
 * A camera-track beat, mirroring `FocusTarget`'s `approach`/`lookAt` shape.
 * `'take'` begins panning the camera there; `'hold'` keeps it there
 * (typically the same approach/lookAt as the preceding `'take'`, held
 * steady rather than still panning); `'release'` hands control back to the
 * player. `approach`/`lookAt` are required for `'take'`/`'hold'` and
 * ignored (should be omitted) for `'release'`.
 */
export interface CameraKey {
  t: number;
  mode: 'take' | 'hold' | 'release';
  /** World-space camera position to pan or snap to. Ignored when
   *  `trackActorId` is set (kept as the fallback if that actor turns out to
   *  have no live transform to read - see `trackActorId`). */
  approach?: [number, number, number];
  /** World-space point the camera should look at. Same fallback role as
   *  `approach` when `trackActorId` is set. */
  lookAt?: [number, number, number];
  /** Skip the pan and snap immediately - a hard cut. */
  instant?: boolean;
  /**
   * Follow a live actor instead of a fixed world-space target - the
   * runtime player resolves `approach`/`lookAt` itself, every frame, from
   * this actor's current position and facing plus `trackDistance`/
   * `trackHeightOffset`, the same "in front of the actor's own head, along
   * its own forward vector" framing Dwindling's snap.ts already uses. Falls
   * back to this key's own `approach`/`lookAt` if the named actor has no
   * live transform to read (e.g. previewing the cutscene without that
   * actor mounted in the scene).
   */
  trackActorId?: string;
  /** Metres out from the tracked actor's own head, along its own forward
   *  vector. Only meaningful with `trackActorId` set. */
  trackDistance?: number;
  /** Added to the tracked actor's own head height. Only meaningful with
   *  `trackActorId` set. */
  trackHeightOffset?: number;
}

/**
 * A post-effects beat. Every field is optional - a keyframe only speaks
 * about what it changes, and sampling holds the last value set for anything
 * it does not mention. Values are absolute engine settings (not deltas), so
 * the caller is responsible for snapshotting whatever the player had before
 * the cutscene and restoring it exactly on handback.
 */
export interface PostKeyframe {
  t: number;
  bloomIntensity?: number;
  vignetteAmount?: number;
  grainOpacity?: number;
  chromaticAberration?: boolean;
}

/** A cue name on the audio track. The host resolves what it means. */
export interface AudioCue {
  t: number;
  cue: string;
}

/** A game-event beat, so state changes land on the frame that was authored. */
export interface Signal {
  t: number;
  event: string;
}

export interface Cutscene {
  id: string;
  /** Seconds. */
  duration: number;
  interruptible: 'never' | 'after-first-view';
  tracks: {
    camera: CameraKey[];
    actors: ActorKey[];
    post: PostKeyframe[];
    audio: AudioCue[];
    signals: Signal[];
  };
}

/**
 * A sanity ceiling on cutscene length, not a design rule.
 *
 * This was 6 seconds, carried over from Dwindling's own cutscenes.md ("a
 * cutscene is a short, diegetic beat, not a video"). That is sound guidance
 * for Dwindling and wrong as a hard limit for every game on the platform:
 * six seconds cannot hold an opening, a reveal or anything trailer-shaped,
 * and `@sstk/intro-kit` had already dropped its equivalent cap for exactly
 * that reason. The line that comment already drew - "individual games may
 * enforce a stricter cap of their own on top of this one" - is the right
 * one, so the shared value is now just a guard against an obviously bogus
 * duration, and a game that wants six seconds still enforces six.
 */
export const CUTSCENE_MAX_DURATION = 60;

/** Throws if a cutscene breaks a hard limit: too long, a key outside its own
 * duration, or a non-release camera key with nowhere to go - neither a
 * fixed approach/lookAt nor a trackActorId. */
export function validateCutscene(cutscene: Cutscene): void {
  if (cutscene.duration > CUTSCENE_MAX_DURATION) {
    throw new Error(
      `Cutscene "${cutscene.id}" is ${cutscene.duration}s, over the ${CUTSCENE_MAX_DURATION}s hard cap.`,
    );
  }
  const allTracks: { t: number }[][] = [
    cutscene.tracks.camera,
    cutscene.tracks.actors,
    cutscene.tracks.post,
    cutscene.tracks.audio,
    cutscene.tracks.signals,
  ];
  for (const track of allTracks) {
    for (const key of track) {
      if (key.t < 0 || key.t > cutscene.duration) {
        throw new Error(`Cutscene "${cutscene.id}" has a key at t=${key.t}, outside [0, ${cutscene.duration}].`);
      }
    }
  }
  for (const key of cutscene.tracks.camera) {
    if (key.mode === 'release') continue;
    const hasFixedTarget = key.approach !== undefined && key.lookAt !== undefined;
    const hasTrackTarget = key.trackActorId !== undefined;
    if (!hasFixedTarget && !hasTrackTarget) {
      throw new Error(
        `Cutscene "${cutscene.id}" has a '${key.mode}' camera key at t=${key.t} with no approach/lookAt or trackActorId.`,
      );
    }
  }
}

/**
 * The active clip per actor at time t: for each `actorId`, the clip from the
 * latest key at or before t, if any.
 *
 * Order-independent. This used to `break` on the first key past `t`, which
 * silently truncated the whole track the moment anything handed it keys out
 * of order - and nothing checked that they were in order, so the result was
 * a quietly wrong cutscene rather than an error. Since the editor is not the
 * only thing that can author a track (a published document, a fixture, a
 * future import), the fix is to stop depending on the ordering at all rather
 * than to police it.
 *
 * Ties on `t` resolve to the key appearing later in the array, matching what
 * the previous ascending walk did.
 */
export function activeActorClips(cutscene: Cutscene, t: number): Record<string, string> {
  const active: Record<string, string> = {};
  const chosenAt: Record<string, number> = {};
  for (const key of cutscene.tracks.actors) {
    if (key.t > t) continue;
    const best = chosenAt[key.actorId];
    if (best === undefined || key.t >= best) {
      active[key.actorId] = key.clipId;
      chosenAt[key.actorId] = key.t;
    }
  }
  return active;
}

/** The camera key in effect at time t: the latest key at or before t, or null
 *  before the first one. Order-independent, for the reason given on
 *  `activeActorClips`; ties resolve to the later array position. */
export function activeCameraKey(cutscene: Cutscene, t: number): CameraKey | null {
  let current: CameraKey | null = null;
  for (const key of cutscene.tracks.camera) {
    if (key.t > t) continue;
    if (current === null || key.t >= current.t) current = key;
  }
  return current;
}

export interface PostState {
  bloomIntensity: number | null;
  vignetteAmount: number | null;
  grainOpacity: number | null;
  chromaticAberration: boolean | null;
}

/**
 * The post-effects state at time t: each field holds whatever the latest key
 * up to t set it to, or null if no key has touched that field yet - null
 * means "the caller's own base value", not "off". Order-independent.
 */
export function samplePost(cutscene: Cutscene, t: number): PostState {
  const state: PostState = {
    bloomIntensity: null,
    vignetteAmount: null,
    grainOpacity: null,
    chromaticAberration: null,
  };
  // Sorted here rather than assumed sorted: a post track is a fold, so the
  // order keys are applied in decides the result. See `activeActorClips`.
  const due = cutscene.tracks.post.filter((key) => key.t <= t).sort((a, b) => a.t - b.t);
  for (const key of due) {
    if (key.bloomIntensity !== undefined) state.bloomIntensity = key.bloomIntensity;
    if (key.vignetteAmount !== undefined) state.vignetteAmount = key.vignetteAmount;
    if (key.grainOpacity !== undefined) state.grainOpacity = key.grainOpacity;
    if (key.chromaticAberration !== undefined) state.chromaticAberration = key.chromaticAberration;
  }
  return state;
}

/** Cue names that became due crossing from `prevT` (exclusive) to `t`
 *  (inclusive), in the order they fall due rather than array order. */
export function dueAudioCues(cutscene: Cutscene, prevT: number, t: number): string[] {
  return cutscene.tracks.audio
    .filter((key) => key.t > prevT && key.t <= t)
    .sort((a, b) => a.t - b.t)
    .map((key) => key.cue);
}

/** Event names that became due crossing from `prevT` (exclusive) to `t`
 *  (inclusive), in the order they fall due rather than array order. */
export function dueSignals(cutscene: Cutscene, prevT: number, t: number): string[] {
  return cutscene.tracks.signals
    .filter((key) => key.t > prevT && key.t <= t)
    .sort((a, b) => a.t - b.t)
    .map((key) => key.event);
}
